import {
  Loading,
  Header,
  Paragraph,
  Checkbox,
  Link,
  Button,
  Tooltip,
  Alert,
} from 'components/patterns'
import { useSelector } from 'hooks/redux'
import PropTypes from 'prop-types'
import { isArray, isString } from 'utils/types'
import React, { useState, useEffect, useCallback } from 'react'
import { getIsLoading, getCollection } from 'selectors/collection'
import { useDispatch } from 'react-redux'
import { getSearch } from 'actions/search'
import {
  FEATURE,
  LOGIC_SETS,
  TIMERS,
  CAN_INPUTS,
  MAX_CAN_INPUTS,
  OBSOLETE,
  DESIGN,
  ACTIVE,
  TEST,
} from 'utils/constants'
import { propEq, assoc, length, prop, sum, pathOr } from 'ramda'
import { getSearchResults } from 'selectors/search'
import { getAuthenticatedModulesForVehicleUpdate } from 'selectors/authorization/checPlus'
import {
  selectVehicleBySerial,
  getModuleList,
  getSwitchesByModuleForVehicle,
  getCanInputsByModuleForVehicle,
} from 'selectors/truck'
import { displayModuleName } from 'i18n/displayConstants'
import strings from 'utils/strings'
import { formatMinDiagnosticVariant } from 'utils/feature'
import {
  ContentContainer,
  TableCell,
  TruncateTableCell,
  TruncateTable,
  ReviewHeader,
  CollectionPartNumber,
  Footer,
} from './FeatureCollections.style'
import { getMaxNotificationString } from '../utils'
import { isLegacyCollection } from './collectionCompatibility'

// Bug 479123 Issue 19: Normalize status from API (e.g., "DESIGN" -> "Design")
const normalizeStatus = (status) => {
  if (!status) return DESIGN
  const statusMap = {
    [DESIGN]: strings.collection_status_design || 'Design',
    [ACTIVE]: strings.collection_status_active || 'Active',
    [OBSOLETE]: strings.collection_status_obsolete || 'Obsolete',
  }
  // Normalize to title case
  const normalized =
    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  return statusMap[normalized] || statusMap[status] || status
}

export const getCompatibilityError = ({
  isCompatible,
  incompatibilityReason,
}) =>
  isCompatible === false
    ? incompatibilityReason || strings.feature_incompatible
    : null

export const selectEnabledFeatures = (features) =>
  features.map((feature) =>
    feature.disabled ? feature : assoc('selected', true)(feature)
  )

export const getCollectionCapacityMessages = (features, moduleCapacities) => ({
  maxLogicsMessage: getMaxNotificationString(
    LOGIC_SETS,
    features,
    moduleCapacities
  ),
  maxTimersMessage: getMaxNotificationString(
    TIMERS,
    features,
    moduleCapacities
  ),
  maxCanInputsMessage: getMaxNotificationString(CAN_INPUTS, features),
})

const CollectionReview = ({
  serialNumber,
  changeStep,
  reset,
  setSelectedFeatures,
}) => {
  const dispatch = useDispatch()

  const [features, setFeatures] = useState(null)
  const [isAllSelected, setAllSelected] = useState(true)
  const [isSearching, setIsSearching] = useState(false)

  const vehicle = useSelector(selectVehicleBySerial, { serialNumber })
  const isLoading = useSelector(getIsLoading)
  const { name, description, partUris, partNumber } =
    useSelector(getCollection) || {}
  const collectionData = useSelector(getCollection) || {}
  const isLegacy = isLegacyCollection(collectionData)
  const results = useSelector(getSearchResults, FEATURE)
  const authenticatedModuleNames = useSelector(
    getAuthenticatedModulesForVehicleUpdate
  )
  const modules = useSelector(getModuleList, { serialNumber })
  const switchIndicatorsByModule = useSelector(getSwitchesByModuleForVehicle, {
    serialNumber,
  })
  const canInputsByModule = useSelector(getCanInputsByModuleForVehicle, {
    serialNumber,
  })

  const validateFeatures = useCallback(
    (records) => {
      const notAuthorized = (record) => {
        const { moduleName } = record

        if (!authenticatedModuleNames.includes(moduleName)) {
          return strings.not_authorized_to_add_module
        }
        return null
      }

      const moduleNotOnVehicle = (record) => {
        if (!modules.includes(record.moduleName)) {
          return strings.module_not_on_vehicle(
            displayModuleName(record.moduleName)
          )
        }
        return null
      }

      const alreadyInUse = (record) => {
        // Check by partNumber since different revisions of the same feature
        // have different IDs but share the same partNumber
        if (
          vehicle.features.some(
            (feature) =>
              feature.partNumber === record.partNumber ||
              feature.id === record.id
          )
        ) {
          return strings.feature_already_on_vehicle
        }
        return null
      }

      const exceedsMaxSwitchCount = (record) => {
        if (!switchIndicatorsByModule[record.moduleName]) return null
        const { maxCount, switches = [] } =
          switchIndicatorsByModule[record.moduleName]
        const switchesFromRecord = record.switchIndicators || []

        const switchesLengthFromRecord = new Set(
          switchesFromRecord.concat(switches)
        ).size

        return maxCount && switchesLengthFromRecord > maxCount
          ? strings.exceeds_maximum_switch_indicators_xmc(record.moduleType)
          : null
      }

      const exceedsMaxCanInputsCount = (record) => {
        if (!canInputsByModule[record.moduleName]) return null
        const { canInputs = [] } = canInputsByModule[record.moduleName]
        const canInputsFromRecord = record.canInputs || []

        const canInputsLengthFromRecord = new Set(
          canInputsFromRecord.concat(canInputs)
        ).size

        const canInputsCountFromModule = pathOr(0, [
          record.moduleName,
          'canInputCount',
        ])
        const maxCount = pathOr(MAX_CAN_INPUTS, [
          record.moduleName,
          'maxCanInputs',
        ])

        return canInputsLengthFromRecord + canInputsCountFromModule > maxCount
          ? strings.exceeds_maximum_can_inputs_xmc(record.moduleType)
          : null
      }

      const isIncompatible = (record) => getCompatibilityError(record)

      const validatedRecords = records.map((record) => {
        const errStrings = [
          notAuthorized,
          moduleNotOnVehicle,
          alreadyInUse,
          exceedsMaxSwitchCount,
          exceedsMaxCanInputsCount,
          isIncompatible,
        ].reduce((validationFns, validateRecordFn) => {
          const valError = validateRecordFn(record)
          let errorMessages = []

          if (valError) {
            if (isArray(valError)) {
              errorMessages = [
                ...errorMessages,
                ...valError.filter((str) => !!str).map((err) => err),
              ]
            }
            if (isString(valError)) {
              errorMessages = [...errorMessages, valError]
            }
          }
          return [...validationFns, ...errorMessages]
        }, [])
        const canBeAdded = record.status === ACTIVE || record.status === TEST
        return {
          ...record,
          errStrings,
          selected: errStrings.length === 0 && canBeAdded,
          disabled: !!errStrings.length || !canBeAdded,
        }
      })

      return validatedRecords
    },
    [
      authenticatedModuleNames,
      modules,
      switchIndicatorsByModule,
      canInputsByModule,
      vehicle.features,
    ]
  )

  useEffect(() => {
    if (!features && length(partUris) && !isSearching) {
      const featureIds = partUris.map((uri) => uri.replace('/features/', ''))
      const body = {
        Search: featureIds.join(' '),
        Serial: serialNumber,
        Statuses: ['active', 'obsolete', 'test', 'new', 'returned'],
        StartIndex: 0,
        Count: 9999,
        FilterConflicts: false,
      }
      setIsSearching(true)
      dispatch(
        getSearch({
          collectionName: FEATURE,
          isSearching: true,
          method: 'POST',
          body,
        })
      )
    }
    if (!features && length(results)) {
      const data = validateFeatures(results)
      setIsSearching(false)
      setFeatures(data)
    }
  }, [
    features,
    partUris,
    setFeatures,
    results,
    dispatch,
    isSearching,
    validateFeatures,
    serialNumber,
  ])

  const handleSelectAll = useCallback(() => {
    const selectAll = !isAllSelected
    // Todo: this should probably be done in a selector
    if (selectAll) {
      // incompatable rps cannot be selected, start selected list with
      const updatedSet = selectEnabledFeatures(features)

      setFeatures(updatedSet)
      setAllSelected(true)
    } else {
      const updatedSet = features.map(assoc('selected', false))
      setFeatures(updatedSet)
      setAllSelected(false)
    }
    setAllSelected(selectAll)
  }, [setAllSelected, features, isAllSelected])

  if (isLoading || isSearching || !features) return <Loading />

  // Capacity helpers compare staged additions with the unchanged vehicle set.
  // Collection candidates are only selected at this point, so mark them in the
  // calculation projection without changing their workflow state.
  const onGoingSet = [
    ...vehicle.features,
    ...features
      .filter(propEq('selected', true))
      .map(assoc('isMarkedForAddition', true)),
  ]

  const { maxLogicsMessage, maxTimersMessage, maxCanInputsMessage } =
    getCollectionCapacityMessages(onGoingSet, vehicle.modules)

  const handleSelect =
    (record) =>
    ({ target: { checked } }) => {
      const updatedSet = features.map((feature) => {
        if (feature.id === record.id) return assoc('selected', checked)(feature)

        return feature
      })

      setFeatures(updatedSet)
      const areAllSelected = !updatedSet.some((f) => !f.selected)
      setAllSelected(areAllSelected)
    }

  const handleAddCollection = () => {
    setSelectedFeatures(features.filter(propEq('selected', true)))
    changeStep(2)
  }

  const handleBack = () => {
    changeStep(0)
    reset()
  }

  const totalLogics = sum(features.map(prop('complexQueryCount')))
  const totalTimers = sum(features.map(prop('timerQueryCount')))
  const overLogics = Boolean(maxLogicsMessage)
  const overTimers = Boolean(maxTimersMessage)
  const overCanInputs = Boolean(maxCanInputsMessage)
  const areAllDeselected = features.every((f) => !f.selected)
  const areAllDisabled = features.every((f) => f.disabled)
  const canInputsTotal = new Set(
    features.flatMap((item) => item.canInputs).filter((item) => item)
  ).size
  const disableAddToCollection =
    areAllDeselected ||
    areAllDisabled ||
    overLogics ||
    overTimers ||
    overCanInputs

  const renderCustomCheckbox = (_, record) => {
    const { errStrings, status } = record

    const errList = errStrings.length ? (
      <ul
        style={{
          width: 'auto',
          whiteSpace: 'nowrap',
          margin: 0,
          paddingLeft: errStrings.length > 1 ? '20px' : 0,
          listStyle: errStrings.length > 1 ? 'disc' : 'none',
        }}
      >
        {errStrings.map((str) => (
          <li key={str}>{str}</li>
        ))}
      </ul>
    ) : null

    const checked = record.selected

    const canBeAdded = status === ACTIVE || status === TEST

    const disabled = !canBeAdded || errStrings.length > 0

    const CheckBox = (
      <Checkbox
        checked={checked}
        className="checkbox"
        name={record.id}
        disabled={disabled}
        onChange={handleSelect(record)}
      />
    )
    return errStrings.length > 0 || !canBeAdded ? (
      <Tooltip
        overlayClassName="autoWidth"
        title={
          !canBeAdded ? strings.obsolete_feature_checkbox_tooltip : errList
        }
        placement="right"
      >
        {CheckBox}
      </Tooltip>
    ) : (
      CheckBox
    )
  }

  const getColumns = () => [
    {
      key: '1',
      title: (
        <Checkbox
          checked={areAllDisabled ? false : isAllSelected}
          disabled={areAllDisabled}
          onChange={handleSelectAll}
        />
      ),
      width: 35,
      className: 'vehicle-feature-table_search-feature-checkbox',
      render: renderCustomCheckbox,
    },
    {
      key: '2',
      title: strings.feature_no,
      width: 140,
      sorter: (a, b) => a.partNumber.localeCompare(b.partNumber),
      render: (_, record) => (
        <Link to={prop('uri', record)} target="_blank">
          {record.partNumber}
        </Link>
      ),
      defaultSortOrder: 'ascend',
    },
    // Bug 538201: Revision column always shown — display-only
    {
      key: '2b',
      title: strings.revision_short_dot,
      width: 50,
      dataIndex: 'revision',
      render: (revision) => revision || '-',
    },
    {
      key: '3',
      title: strings.description,
      ellipsis: true,
      width: 200,
      sorter: (a, b) => a.description.localeCompare(b.description),
      render: (_, record) => (
        <TruncateTableCell disabled={record.disabled}>
          {record.description}
        </TruncateTableCell>
      ),
    },
    {
      key: '4',
      title: strings.ecu,
      width: 80,
      sorter: (a, b) => a.moduleName.localeCompare(b.moduleName),
      render: (_, record) => (
        <TableCell disabled={record.disabled}>{record.moduleName}</TableCell>
      ),
    },
    {
      key: '5',
      title: strings.minimum_diagnostic_variant,
      width: 100,
      render: (_, record) => (
        <TableCell disabled={record.disabled}>
          {formatMinDiagnosticVariant(record.minCompatibleVariant)}
        </TableCell>
      ),
    },
    {
      key: '6',
      title: strings.master_no,
      width: 100,
      render: (_, record) => (
        <TableCell disabled={record.disabled}>{record.masterNumber}</TableCell>
      ),
    },
    {
      key: '7',
      title: strings.release_no,
      width: 100,
      render: (_, record) => (
        <TableCell disabled={record.disabled}>{record.releaseNumber}</TableCell>
      ),
    },
    // Bug 538201: Status column always shown — display-only
    {
      key: '8',
      title: strings.status,
      width: 100,
      // Bug 479123 Issue 19: Normalize status to title case
      render: (_, record) => (
        <TableCell disabled={record.disabled}>
          {normalizeStatus(record.status)}
        </TableCell>
      ),
    },
    {
      key: '9',
      title: (
        <ResourceCounter
          text={strings.logic_sets_used}
          count={totalLogics}
          isOver={overLogics}
        />
      ),
      width: 100,
      render: (_, record) => (
        <TableCell disabled={record.disabled}>
          {record.complexQueryCount}
        </TableCell>
      ),
    },
    {
      key: '10',
      title: (
        <ResourceCounter
          text={strings.timers_used}
          count={totalTimers}
          isOver={overTimers}
        />
      ),
      width: 100,
      render: (_, record) => (
        <TableCell disabled={record.disabled}>
          {record.timerQueryCount}
        </TableCell>
      ),
    },
    {
      dataIndex: 'canInputs',
      width: 100,
      title: (
        <ResourceCounter
          text={strings.can_inputs_used}
          count={canInputsTotal}
          isOver={overCanInputs}
        />
      ),
      render: (canInputs) => (
        <span>{!canInputs ? strings.n_a : canInputs.length}</span>
      ),
    },
    {
      title: <span>{strings.switch_indicators_used}</span>,
      dataIndex: 'switchIndicators',
      width: 125,
      render: (switchIndicators) => (
        <span>{!switchIndicators ? strings.n_a : switchIndicators.length}</span>
      ),
    },
  ]

  return (
    <>
      <ContentContainer className="px-18">
        <>
          {[
            {
              showWarning: Boolean(maxLogicsMessage),
              message: maxLogicsMessage,
            },
            {
              showWarning: Boolean(maxTimersMessage),
              message: maxTimersMessage,
            },
            {
              showWarning: Boolean(maxCanInputsMessage),
              message: maxCanInputsMessage,
            },
          ]
            .filter((item) => item.showWarning)
            .map((item) => (
              <React.Fragment key={item.message}>
                <Alert
                  type="warning"
                  persistent
                  closable={false}
                  message={item.message}
                />
                <br />
              </React.Fragment>
            ))}
        </>
        <ReviewHeader>
          {/* Issues 6 & 7 (#496741): Title at 18px (h3) and part number at 14px
              as separate elements, no dash separator, both in Black Secondary. */}
          <Header type="h3" color="rgba(7, 7, 15, 0.7)">
            {name}
          </Header>
          {partNumber && (
            <CollectionPartNumber>{partNumber}</CollectionPartNumber>
          )}
          <Paragraph>{description}</Paragraph>
        </ReviewHeader>

        {/* Bug 499041 Issue 26: Use standard Alert warning to match app-wide styling */}
        {isLegacy && (
          <Alert
            type="warning"
            message={strings.legacy_collection_warning}
            closable={false}
            persistent
          />
        )}

        <TruncateTable
          className="truncate-table-fixed-layout"
          columns={getColumns()}
          dataSource={features}
          rowKey={(record, index) => `${record.id}-${index}`}
        />

        <Footer>
          <Button text={strings.back} onClick={handleBack} type="secondary" />
          <Button
            text={strings.add_collection_to_vehicle}
            onClick={handleAddCollection}
            iconProps={{ icon: 'add', fill: '#fff', size: 10 }}
            disabled={disableAddToCollection}
          />
        </Footer>
      </ContentContainer>
    </>
  )
}

CollectionReview.propTypes = {
  serialNumber: PropTypes.string,
  changeStep: PropTypes.func,
  reset: PropTypes.func,
  setSelectedFeatures: PropTypes.func,
}

export default CollectionReview

const ResourceCounter = ({ text, count, isOver }) => {
  const bkg = isOver ? '#94040a' : 'transparent'
  const fontColor = isOver ? '#fff' : '#07070fb3'
  const sup = (
    <sup>
      <span
        style={{
          background: bkg,
          color: fontColor,
          padding: 2,
          position: 'relative',
          top: 4,
          marginLeft: 2,
        }}
      >
        {count}
      </span>
    </sup>
  )

  return (
    <span>
      {text}
      {sup}
    </span>
  )
}

ResourceCounter.propTypes = {
  text: PropTypes.string,
  count: PropTypes.number,
  isOver: PropTypes.bool,
}
